import { useEffect, useState } from 'react';
import styles from './ContactForm.module.scss';
import {
	Button,
	Heading,
	Paragraph,
	Form,
	FormfieldSelect,
	FormfieldString,
	FormfieldTextarea,
	FormfieldGroup,
	Spinner,
	Option,
} from 'ui/components';
import { CategoryFilter } from '../CategoryFilter';
import { useForm, SubmitHandler, useFormState } from 'react-hook-form';
import classNames from 'classnames';
import IconArrowRight from 'ui/icons/icon-arrow-right-web.svg';
import logger from 'helpers/logger';

interface FormData {
	Description?: string;
	Email?: string;
	FirstName?: string;
	LastName?: string;
	Phone?: string;
	Company?: string;
	City?: string;
	State?: string;
	Country?: string;
	UserCaptcha?: string;
	Topic?: string;
	ReCaptchaToken?: string;
	UseAlternativeCaptcha?: boolean;
}

export interface ContactFormProps {
	className?: string;
	formHeadingId?: string; // ID for the form heading, used for accessibility
	onFormSubmit: (data: FormData) => void;
	dictionary?: { [key: string]: string };
	isLoading?: boolean;
	formIsSent?: boolean;
	formSubmitError?: boolean;
	reCaptchaApiKey?: string | undefined;
	useAlternativeCaptcha?: boolean;
	topics: Models.CategoryFilterItem[];
	countries?: Option[];
	privacyPolicyLink?: string;
	globalContactLink?: string;
	alternativeContactLink?: string;
	onReCaptchaLoaded?: () => void;
}

declare global {
	interface Window {
		grecaptcha: any;
	}
}

export const ContactForm: React.FC<ContactFormProps> = ({
	className,
	onFormSubmit,
	formHeadingId,
	dictionary,
	isLoading,
	formIsSent,
	formSubmitError,
	reCaptchaApiKey,
	useAlternativeCaptcha = false,
	topics,
	countries,
	privacyPolicyLink,
	globalContactLink,
	alternativeContactLink,
	onReCaptchaLoaded,
}) => {
	// FORM HANDLING
	const [errorSummary, setErrorSummary] = useState(0);
	const [selectedCountry, setSelectedCountry] = useState('');
	const { register, handleSubmit, control, getValues, reset } = useForm<FormData>({
		defaultValues: {
			Country: '',
		},
		mode: 'onBlur',
	});
	const [selectedTopic, setSelectedTopic] = useState('');
	const formId = formHeadingId ? formHeadingId : undefined;

	/* eslint-disable @typescript-eslint/no-var-requires */
	const reactSimpleCaptcha = require('react-simple-captcha');
	const { LoadCanvasTemplate } = reactSimpleCaptcha;
	const { loadCaptchaEnginge } = reactSimpleCaptcha;
	const { validateCaptcha } = reactSimpleCaptcha;

	if (typeof window !== 'undefined') {
		window.grecaptcha = window.grecaptcha || {};
	}

	const [scriptLoaded, setScriptLoaded] = useState(false);
	const [isGeneratingToken, setIsGeneratingToken] = useState(false);
	const [captchaError, setCaptchaError] = useState<string | null>(null);

	// Helper function to generate fresh token
	const generateReCaptchaToken = (): Promise<string> => {
		return new Promise((resolve, reject) => {
			if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
				window.grecaptcha.ready(() => {
					window.grecaptcha
						.execute(reCaptchaApiKey)
						.then((token: string) => {
							resolve(token);
						})
						.catch((error: any) => {
							reject(error);
						});
				});
			} else {
				reject(new Error('grecaptcha not available'));
			}
		});
	};

	// Load captcha script when form is visible
	useEffect(() => {
		if (formIsSent || scriptLoaded) return;

		// Users from restricted regions get canvas captcha
		if (useAlternativeCaptcha) {
			const reloadLink = document.getElementById('reload_href');
			if (reloadLink) {
				reloadLink.textContent = dictionary.formCaptchaReload;
			}
			const canvElement = document.getElementById('canv');
			if (canvElement) canvElement.style.border = '1px solid black';
			loadCaptchaEnginge(6);
			setScriptLoaded(true);
			return;
		}

		// Check if reCAPTCHA script already exists
		const existingScript = document.querySelector(`script[src*="recaptcha/api.js"]`);
		if (existingScript) {
			setScriptLoaded(true);
			onReCaptchaLoaded?.();
			return;
		}

		// Load reCAPTCHA script dynamically
		const script = document.createElement('script');
		script.src = `https://www.google.com/recaptcha/api.js?render=${reCaptchaApiKey}`;
		script.addEventListener('load', () => {
			setScriptLoaded(true);
			onReCaptchaLoaded?.();
		});
		script.addEventListener('error', () => {
			logger.error('Failed to load reCAPTCHA script', { siteKey: reCaptchaApiKey });
			setCaptchaError(dictionary.formCaptchaTokenError);
		});
		document.body.appendChild(script);
	}, [formIsSent, scriptLoaded, onReCaptchaLoaded, useAlternativeCaptcha, reCaptchaApiKey, dictionary]);

	const onSubmit: SubmitHandler<FormData> = async (data) => {
		setCaptchaError(null);

		const topicId = topics?.find((topic) => topic.children === selectedTopic)?.id ?? '';

		const formattedDataForCN = {
			...data,
			Country: data.Country?.toLowerCase(), // Backend expects lowercase
			Topic: topicId,
		};

		// Handle alternative captcha (canvas-based)
		if (useAlternativeCaptcha) {
			if (!validateCaptcha(getValues('UserCaptcha'))) {
				setCaptchaError(dictionary.formCaptchaInvalid);
				loadCaptchaEnginge(6);
				return;
			}
			// Strip UserCaptcha — validated client-side only, server cannot verify it
			const { UserCaptcha: _captcha, ...dataWithoutCaptcha } = formattedDataForCN;
			onFormSubmit({ ...dataWithoutCaptcha, UseAlternativeCaptcha: true });
			reset();
			return;
		}

		// Handle reCAPTCHA validation
		if (!scriptLoaded) {
			logger.error('reCAPTCHA script not loaded on form submit');
			setCaptchaError(dictionary.formErrorMessage);
			return;
		}

		try {
			setIsGeneratingToken(true);
			const freshToken = await generateReCaptchaToken();

			onFormSubmit({ ...data, Topic: topicId, ReCaptchaToken: freshToken });
			reset();
		} catch (error) {
			logger.error('Failed to generate reCAPTCHA token', error);
			setCaptchaError(dictionary.formCaptchaTokenError);
		} finally {
			setIsGeneratingToken(false);
		}
	};

	const handleTopicChanged = (topicItem: Models.CategoryFilterItem) => {
		setSelectedTopic(topicItem.children);
	};

	const { errors } = useFormState({
		control,
	});

	const onError = (data) => {
		const totalErrors = Object.keys(data).length;
		setErrorSummary(totalErrors);
	};

	// Populate country selector with all countries
	const countrySelectOptions = [
		{
			label: '',
			value: '',
			text: dictionary?.formCountryPlaceholder || '',
			hidden: false,
			disabled: false,
		},
		...countries.map(
			(obj: Option | { key: string; value: string }) =>
				({
					value: 'key' in obj ? String(obj.key) : String(obj.value),
					text: 'text' in obj ? String(obj.text) : String(obj.value),
					disabled: false,
					hidden: false,
					selected: selectedCountry === ('key' in obj ? String(obj.key) : String(obj.value)) ? true : false,
				} as Option),
		),
	];

	// / FORM HANDLING
	// NO NUMBERS REGEX
	const regexNoNumbers = new RegExp(/^([^0-9]*)$/);
	const textOnlyInputRules = {
		pattern: {
			value: regexNoNumbers,
			message: 'Numbers are not allowed here',
		},
	};
	const textOnlyInputKeyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
		const regex = regexNoNumbers;

		// preventDefault if the key pressed is a number
		if (!regex.test(event.key)) {
			event.preventDefault();
		}
	};

	// VALID PHONE NUMBER REGEX
	const regexValidPhoneNumber = new RegExp(/^[\d()\-+]+$/);
	const validPhoneNumberInputRules = {
		pattern: {
			value: regexValidPhoneNumber,
			message: 'Phone number is invalid',
		},
	};
	const validPhoneNumberInputKeyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
		const regex = regexValidPhoneNumber;
		if (!regex.test(event.key)) {
			event.preventDefault();
		}
	};

	return (
		<>
			<Heading headingLevel="h2" style="displaySm" id={formId} className={classNames(styles.ContactForm_heading)}>
				{formIsSent ? dictionary?.formThankYouHeading : dictionary?.formHeading}
			</Heading>
			{!formIsSent ? (
				<>
					{dictionary?.formManchetText && (
						<Paragraph variant="website" className="u-bottom-margin--lg">
							<span
								dangerouslySetInnerHTML={{
									__html: dictionary?.formManchetText,
								}}
							></span>
						</Paragraph>
					)}
					{dictionary?.formGlobalContactText && (
						<Paragraph variant="website" className={classNames(styles.ContactForm_alternativeTextContainer, 'u-bottom-margin--lg')}>
							<span
								dangerouslySetInnerHTML={{
									__html: dictionary?.formGlobalContactText + ' ' + globalContactLink,
								}}
							></span>
						</Paragraph>
					)}
				</>
			) : (
				<>
					{dictionary?.formSentDescription && (
						<Paragraph variant="website" className="u-bottom-margin--lg">
							<span
								dangerouslySetInnerHTML={{
									__html: dictionary?.formSentDescription,
								}}
							></span>
						</Paragraph>
					)}
					{dictionary?.formSentAlternativeText && (
						<Paragraph variant="website" className={classNames(styles.ContactForm_alternativeTextContainer, 'u-bottom-margin--lg')}>
							<span
								dangerouslySetInnerHTML={{
									__html: dictionary?.formSentAlternativeText + ' ' + alternativeContactLink,
								}}
							></span>
						</Paragraph>
					)}
				</>
			)}
			{!formIsSent && (
				<>
					<Form
						className={classNames(styles.ContactForm, className)}
						onSubmit={handleSubmit(onSubmit, onError)}
						errorSummary={errorSummary}
						errorSummaryPosition="bottom"
					>
						<CategoryFilter
							smallHeading={dictionary?.formTopicsHeading}
							variant="website"
							layout="grid"
							columns={3}
							categoryFilterItems={topics}
							handleSelectionChanged={handleTopicChanged}
							className={classNames(styles.ContactForm_topics)}
							sortingParameter={selectedTopic}
						/>
						<FormfieldGroup
							id="inquery"
							name="inquery"
							ariaLabelledby={dictionary?.formHeading}
							label={dictionary?.formHeading}
							className={styles.ContactForm_fields}
						>
							<FormfieldTextarea
								id="Description"
								name="Description"
								label={dictionary?.formDescription}
								labelStyle="normalWeb"
								variant="website"
								register={register}
								placeholder={dictionary?.formDescriptionPlaceholder}
								className={classNames(styles.ContactForm_textarea)}
							></FormfieldTextarea>
							{/* Contact details section */}
							<FormfieldGroup
								id="contact-details"
								name="contact-details"
								className={classNames(styles.ContactForm_fields, styles.ContactForm_gridColumns)}
							>
								<FormfieldString
									id="FirstName"
									name="FirstName"
									label={dictionary?.formFirstName}
									type="text"
									register={register}
									className={classNames(styles.ContactForm__field)}
									labelStyle="normalWeb"
									layout="column"
									variant="website"
									placeholder={dictionary?.formFirstNamePlaceholder}
									state={
										errors.FirstName
											? {
													hasError: true,
													required: true,
											  }
											: { required: true }
									}
									errorMessage={errors.FirstName?.message}
									rules={textOnlyInputRules}
									handleKeyPress={textOnlyInputKeyPressHandler}
								/>
								<FormfieldString
									id="LastName"
									name="LastName"
									label={dictionary?.formLastName}
									type="text"
									register={register}
									className={classNames(styles.ContactForm__field)}
									layout="column"
									variant="website"
									labelStyle="normalWeb"
									placeholder={dictionary?.formLastNamePlaceholder}
									state={
										errors.LastName
											? {
													hasError: true,
													required: true,
											  }
											: { required: true }
									}
									errorMessage={errors.LastName?.message}
									rules={textOnlyInputRules}
									handleKeyPress={textOnlyInputKeyPressHandler}
								/>
								<FormfieldString
									id="Email"
									name="Email"
									label={dictionary?.formEmail}
									type="email"
									register={register}
									labelStyle="normalWeb"
									className={classNames(styles.ContactForm__field)}
									layout="column"
									variant="website"
									state={
										errors.Email
											? {
													hasError: true,
													required: true,
											  }
											: { required: true }
									}
									errorMessage={errors.Email?.message}
									placeholder={dictionary?.formEmailPlaceholder}
								/>
								<FormfieldString
									id="Phone"
									name="Phone"
									label={dictionary?.formPhoneNumber}
									type="text"
									register={register}
									className={classNames(styles.ContactForm__field)}
									layout="column"
									variant="website"
									placeholder={dictionary?.formPhoneNumberPlaceholder}
									labelStyle="normalWeb"
									state={
										errors.Phone
											? {
													hasError: true,
													required: true,
											  }
											: { required: true }
									}
									errorMessage={errors.Phone?.message}
									rules={validPhoneNumberInputRules}
									handleKeyPress={validPhoneNumberInputKeyPressHandler}
								/>
								<FormfieldString
									id="Company"
									name="Company"
									label={dictionary?.formCompanyName}
									type="text"
									register={register}
									className={classNames(styles.ContactForm__field)}
									placeholder={dictionary?.formCompanyNamePlaceholder}
									layout="column"
									variant="website"
									labelStyle="normalWeb"
									state={
										errors.Company
											? {
													hasError: true,
													required: true,
													hasTextNoWrap: true,
											  }
											: { required: true, hasTextNoWrap: true }
									}
									errorMessage={errors.Company?.message}
								/>
								<FormfieldString
									id="State"
									name="State"
									label={dictionary?.formState}
									placeholder={dictionary?.formStatePlaceholder}
									type="text"
									register={register}
									className={classNames(styles.ContactForm__field)}
									layout="column"
									variant="website"
									labelStyle="normalWeb"
									state={
										errors.State
											? {
													hasError: true,
													required: false,
											  }
											: { required: false }
									}
									errorMessage={errors.State?.message}
								/>
								<FormfieldSelect
									id="Country"
									name="Country"
									label={dictionary?.formCountry}
									labelStyle="normalWeb"
									variant="website"
									options={countrySelectOptions}
									className={classNames(styles.ContactForm__field)}
									layout="column"
									register={register}
									state={
										errors.Country
											? {
													hasError: true,
													required: true,
											  }
											: { required: true }
									}
									errorMessage={errors.Country?.message}
								/>
								<FormfieldString
									id="City"
									name="City"
									label={dictionary?.formCity}
									placeholder={dictionary?.formCityPlaceholder}
									type="text"
									register={register}
									className={classNames(styles.ContactForm__field)}
									layout="column"
									variant="website"
									labelStyle="normalWeb"
									state={
										errors.City
											? {
													hasError: true,
													required: true,
											  }
											: { required: true }
									}
									errorMessage={errors.City?.message}
								/>
							</FormfieldGroup>
						</FormfieldGroup>
						{/* Captcha */}
						{useAlternativeCaptcha ? (
							<div className={classNames(styles.ContactForm_alternativeCaptchaContainer)}>
								<LoadCanvasTemplate />
								<FormfieldString
									id="UserCaptcha"
									name="UserCaptcha"
									type="text"
									state={
										errors.UserCaptcha
											? {
													hasError: true,
													required: true,
											  }
											: { required: true }
									}
									register={register}
									label={dictionary?.formCaptcha}
									layout="column"
									labelStyle="normalWeb"
									variant="website"
								/>
							</div>
						) : (
							<div className="g-recaptcha" data-sitekey={reCaptchaApiKey} data-size="invisible" />
						)}

						{captchaError && (
							<Paragraph
								variant="website"
								className={classNames(styles.ContactForm_formErrorMessage, 'u-top-margin--md', 'u-bottom-margin--md')}
							>
								{captchaError}
							</Paragraph>
						)}
						{/* Submit */}
						<div className={classNames(styles.ContactForm_buttonWrapper, 'u-top-margin--xxl', 'u-bottom-margin--xxl')}>
							{isLoading || isGeneratingToken ? (
								<Spinner center size="small" color="dark" speed="normal" />
							) : (
								<Button
									style="primaryWeb"
									type="submit"
									withBackIcon={<IconArrowRight />}
									title={dictionary?.formSend}
									aria-label={dictionary?.formSend}
									className={classNames(styles.ContactForm_button)}
									iconClassName={styles.ContactForm_buttonIcon}
								>
									{dictionary?.formSend}
								</Button>
							)}
						</div>
						<div className={styles.ContactForm_formNotes}>
							<Paragraph variant="website" className={classNames(styles.ContactForm_formNotes, 'u-bottom-margin--lg')}>
								<span
									dangerouslySetInnerHTML={{
										__html: dictionary?.formNotes + ' ' + privacyPolicyLink,
									}}
								></span>
							</Paragraph>
						</div>
					</Form>
				</>
			)}
		</>
	);
};
